import { Button, Icon, Modal, Spinner } from "@thetreep/ui";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import JourneyDisplay from "@pages/booking/cart/journey/Journey";
import { timestampDate } from "@bufbuild/protobuf/wkt";
import { CartItem } from "@gen/travel/v1/cart_pb";
import i18n from "@libs/i18n";
import { useState } from "react";
import { useToaster } from "@hooks/useToaster";
import { ConnectError } from "@connectrpc/connect";
import { useNavigate } from "react-router";
import { routes } from "@routes";
import { ActionState } from "@gen/travel/v1/cart_pb";
import { Place } from "@gen/place/v1/place_pb";
import { useCartStore } from "@stores/cartStore";
import { localDateTimeToDate } from "@utils/date";
import { useModal } from "@hooks/useModal";

type TravelDisplayProps = {
  item: CartItem;
  isEmptyingCart: boolean;
};

export function TravelDisplay({ item, isEmptyingCart }: TravelDisplayProps) {
  const { t } = useTranslation();
  const { addToast } = useToaster();
  const navigate = useNavigate();
  const [isReleasing, setIsReleasing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const { release, confirm } = useCartStore();
  const { isOpen, openModal, closeModal } = useModal();

  const actionType =
    item.actionState === ActionState.PAYMENT_REQUIRED
      ? "payment"
      : item.actionState === ActionState.CONFIRMATION_REQUIRED
        ? "confirmation"
        : undefined;

  const travel = item.travel;

  if (
    !travel ||
    !travel.outward?.schedule?.departure ||
    !travel.inward?.schedule?.arrival
  ) {
    return null;
  }

  const startDate = localDateTimeToDate(travel.outward?.schedule?.departure);
  const endDate = localDateTimeToDate(travel.inward?.schedule?.arrival);
  const confirmationTimeLimit = travel.confirmationDeadline
    ? timestampDate(travel.confirmationDeadline)
    : undefined;

  function formatDate(date: Date) {
    return Intl.DateTimeFormat(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  }

  async function handleRelease() {
    if (!travel) return;
    closeModal();
    setIsReleasing(true);
    try {
      await release(travel);
    } catch (e) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    }
    setIsReleasing(false);
  }

  function handlePayment() {
    if (item.paymentData?.data) {
      navigate("/booking/payment", {
        state: {
          paymentData: item.paymentData.data,
          amountDetails: item.travel?.amountDetails,
        },
      });
    }
  }
  async function handleConfirm() {
    if (!item.travel) return;
    try {
      setIsConfirming(true);
      const res = await confirm(item.travel);
      navigate(routes.CONFIRMATION, {
        state: { travel: res, context: "initial" },
      });
    } catch (e) {
      console.error(e);
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsConfirming(false);
    }
  }

  const handleValidate = () => {
    if (actionType === "payment") {
      handlePayment();
    } else if (actionType === "confirmation") {
      handleConfirm();
    }
  };

  function formatPrice(price: number | undefined) {
    const formatter = new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: item.travel?.amountDetails?.currency || "EUR",
    });
    return formatter.format(Number(price) / 100);
  }

  function displayCity(place: Place | undefined) {
    if (!place) return "";
    return place.city || place.name;
  }

  return (
    <div className="bg-white rounded-lg p-6 gap-6 lg:p-8 lg:gap-10">
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        onConfirm={handleRelease}
        content={{
          type: "confirm",
          title: t("cart.releaseConfirmation"),
        }}
        size="default"
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
          <h2 className="font-jakarta text-[14px] font-bold lg:text-[20px] text-text-strong">
            {`${t("cart.outward")} ${displayCity(
              travel.outward.route?.origin,
            )} → ${displayCity(
              travel.outward.route?.destination,
            )}, ${formatDate(startDate)}`}
            <br />
            {`${t("cart.return")} ${displayCity(
              travel.inward.route?.origin,
            )} → ${displayCity(travel.inward.route?.destination)}, ${formatDate(
              endDate,
            )}`}
          </h2>
          <div className="flex items-center justify-between gap-2">
            {confirmationTimeLimit && (
              <p className="text-[14px] text-text-soft">
                {t("cart.guaranteedUpTo")}{" "}
                {format(
                  confirmationTimeLimit,
                  `dd/MM '${t("cart.at")}' HH'${t(
                    "cart.hoursMinutesSeparator",
                  )}'mm`,
                )}
              </p>
            )}
            <button
              disabled={isReleasing || isEmptyingCart}
              className="border border-border-strong rounded-lg p-2"
              onClick={openModal}
            >
              {isReleasing || isEmptyingCart ? (
                <Spinner $size={18} />
              ) : (
                <Icon src="trash" size={18} color="border-strong" />
              )}
            </button>
          </div>
        </div>

        <JourneyDisplay
          key={item.travel?.id + "-1"}
          travel={travel}
          journey={travel.outward}
        />
        <JourneyDisplay
          key={item.travel?.id + "-2"}
          travel={travel}
          journey={travel.inward}
        />
      </div>
      <div className="flex flex-col gap-4 mt-4 text-text-strong">
        <div className="flex text-base font-bold lg:text-[24px] font-jakarta justify-between">
          {t("cart.totalTravel")} :{" "}
          <span>
            {formatPrice(item.travel?.amountDetails?.outOfPocketAmount)}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
            {t("cart.coveredAmount")} (
            {item.travel?.amountDetails?.coveragePercentage}
            %) :{" "}
            <span>
              {formatPrice(item.travel?.amountDetails?.coveredAmount)}
            </span>
          </p>
          <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
            {t("cart.agencyFees") + " (HT)"} :{" "}
            <span>{formatPrice(item.travel?.amountDetails?.agencyFees)}</span>
          </p>
          <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
            {t("cart.agencyFees") + " (TTC)"} :{" "}
            <span>
              {formatPrice(item.travel?.amountDetails?.includedTaxesAgencyFees)}
            </span>
          </p>
          <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
            {t("cart.initialPrice")} :{" "}
            <span>
              {formatPrice(
                item.travel?.amountDetails?.includedTaxesTotalAmount,
              )}
            </span>
          </p>
        </div>
        <div className="flex lg:justify-end [&_button]:w-full lg:[&_button]:w-auto">
          <Button
            variant="primary"
            disabled={isConfirming || isEmptyingCart || isReleasing}
            isLoading={isConfirming}
            onClick={handleValidate}
            endEnhancer={<Icon src="arrow_right_line" color="white" />}
          >
            {actionType == "payment"
              ? t("cart.payTrip")
              : t("cart.confirmTrip")}
          </Button>
        </div>
      </div>
    </div>
  );
}
